import React, { useState, useEffect, FC } from 'react';

import { AutoPayIcon } from '../../AutoPayIcon';
import { AutoPaySecondAttemptContent } from '../../AutoPaySecondAttemptContent';
import { AutoPayFeatureList } from '../components/AutoPayFeatureList/AutoPayFeatureList';
import { AutoPayHeader } from '../components/AutoPayHeader/AutoPayHeader';
import { BankConnectTipsModal } from 'src/commons/components/BankConnectTipsModal';
import Card, { LoadingCard, ErrorCard } from 'src/commons/components/Card';
import { PlaidLink } from 'src/commons/components/PlaidLink';
import { BankConnectionAuthorizationLegalDocument } from 'src/commons/constants';

import {
  PlaidErrorHeading,
  PlaidErrorCard,
  PlaidErrorContainer,
  BankErrorCard,
  BankErrorContainer,
  BankErrorHeading,
  BankErrorSubHeading,
  BankErrorDetailsButton,
  BankAuthorizationAgreement,
  BankAuthorizationAgreementDownloadLink,
  AutoPayActionsContainer,
  PlaidLinkButtonContainer,
  DeclineButton,
} from './styles';

const PlaidBankErrorCopy = (bankName?: string) => (
  <PlaidErrorHeading>
    Unfortunately, Plaid is unable to connect to this&nbsp;
    {bankName ?? 'bank'}
    &nbsp;account. Are you able to try a different account at this bank or
    another?
  </PlaidErrorHeading>
);

interface AutoPaySetupUnconditionalProps {
  loading: boolean;
  showFetchError: boolean;
  failedBankName: string;
  authorized: boolean;
  plaidShouldBeOpen: boolean;
  bcaDocument: BankConnectionAuthorizationLegalDocument;
  resumeAutoPay: Function;
  onPlaidConsent: Function;
  consentInProgress: boolean;
  onPlaidSuccess: (
    publicToken: string,
    accountId: string,
    institutionName?: string
  ) => void;
  onPlaidExit: Function;
  onPlaidOpenClick: Function;
  saveAutoPayPreferencesDeclined: Function;
  submitting: boolean;
}

// AutoPay Setup for applications that DO NOT require a bank account
export const AutoPaySetupUnconditional: FC<AutoPaySetupUnconditionalProps> = ({
  loading,
  showFetchError,
  failedBankName,
  authorized,
  plaidShouldBeOpen,
  consentInProgress,
  bcaDocument,
  resumeAutoPay,
  onPlaidConsent,
  onPlaidSuccess,
  onPlaidExit,
  onPlaidOpenClick,
  saveAutoPayPreferencesDeclined,
  submitting,
}) => {
  const [savingConnectedBankInfo, setSavingConnectedBankInfo] = useState(false);
  const [bankConnectErrorBanner, setBankConnectErrorBanner] = useState({
    display: false,
    institution: null,
  });
  const [displayGenericBankErrorBanner, setDisplayGenericBankErrorBanner] =
    useState(false);
  const [displayBankConnectTipsModal, setDisplayBankConnectTipsModal] =
    useState(false);
  const [showSecondAttemptCard, setShowSecondAttemptCard] = useState(false);

  // Invoke success callback with connected bank info to trigger BANK_CONNECTED workflow
  const handleSuccessLink = (
    publicToken: string,
    accountId: string,
    institutionName?: string
  ) => {
    setSavingConnectedBankInfo(true);
    onPlaidSuccess(publicToken, accountId, institutionName);
  };

  const handleCloseLink = () => {
    window.scrollTo(0, 0);
    // Show the generic error banner (only)
    setBankConnectErrorBanner({ display: false, institution: '' });
    setDisplayGenericBankErrorBanner(true);
    onPlaidExit();
  };

  // when the user chooses to skip autopay
  const handleSkipClick = () => {
    // if they've already skipped
    if (showSecondAttemptCard) {
      // proceed
      saveAutoPayPreferencesDeclined();
    } else {
      // otherwise show the second attempt card
      setShowSecondAttemptCard(true);
    }
  };

  useEffect(() => {
    resumeAutoPay();
  }, []);

  useEffect(() => {
    // Display bank error upon PLAID_SELECTED_ACCOUNT_NOT_FOUND error on workflow event
    // failedBankName is only never null after BANK_CONNECTED workflow failure
    if (failedBankName !== null) {
      setDisplayGenericBankErrorBanner(false);
      setBankConnectErrorBanner({ display: true, institution: failedBankName });
      window.scrollTo(0, 0);
    }
  }, [failedBankName]);

  if ((loading || savingConnectedBankInfo) && failedBankName === null) {
    return <LoadingCard />;
  }

  if (showFetchError) {
    return <ErrorCard />;
  }

  return (
    <>
      <BankConnectTipsModal
        showModal={displayBankConnectTipsModal}
        setShowModal={setDisplayBankConnectTipsModal}
      />
      {bankConnectErrorBanner.display && (
        <PlaidErrorCard>
          <PlaidErrorContainer>
            {PlaidBankErrorCopy(bankConnectErrorBanner.institution)}
          </PlaidErrorContainer>
        </PlaidErrorCard>
      )}
      {displayGenericBankErrorBanner && (
        <BankErrorCard>
          <BankErrorContainer>
            <BankErrorHeading>
              Having trouble connecting to your bank?
            </BankErrorHeading>
            <BankErrorSubHeading>
              Here are some tips to get you unstuck
            </BankErrorSubHeading>
          </BankErrorContainer>
          <BankErrorDetailsButton
            id="bank-error-details-button"
            aria-label="Show More Details On Connecting Your Bank"
            type="button"
            onClick={() => setDisplayBankConnectTipsModal(true)}
          >
            See details
          </BankErrorDetailsButton>
        </BankErrorCard>
      )}
      <Card id="autopay-setup-unconditional-card">
        {showSecondAttemptCard ? (
          <AutoPaySecondAttemptContent />
        ) : (
          <>
            <AutoPayIcon />
            <AutoPayHeader />

            <AutoPayFeatureList />
          </>
        )}
        <BankAuthorizationAgreement>
          By setting up AutoPay you agree to the&nbsp;
          <BankAuthorizationAgreementDownloadLink
            id="bank-connection-authorization-doc-link"
            target="_blank"
            href={bcaDocument.viewUrl}
            aria-label={`View document ${bcaDocument.title}`}
          >
            {bcaDocument.title}
          </BankAuthorizationAgreementDownloadLink>
        </BankAuthorizationAgreement>
        <AutoPayActionsContainer shorterBottomMargin={showSecondAttemptCard}>
          <PlaidLinkButtonContainer>
            <PlaidLink
              authorized={authorized}
              plaidShouldBeOpen={plaidShouldBeOpen}
              onConsent={onPlaidConsent}
              consentInProgress={consentInProgress}
              onSuccess={(publicToken, accountId, institutionName) => {
                handleSuccessLink(publicToken, accountId, institutionName);
              }}
              onOpenClick={onPlaidOpenClick}
              onExit={() => handleCloseLink()}
            >
              Set up AutoPay
            </PlaidLink>
          </PlaidLinkButtonContainer>
          <DeclineButton
            id={
              showSecondAttemptCard
                ? 'autopay-second-attempt-unconditional-decline-button'
                : 'autopay-unconditional-decline-button'
            }
            onClick={handleSkipClick}
            disabled={submitting}
          >
            Skip AutoPay for now
          </DeclineButton>
        </AutoPayActionsContainer>
      </Card>
    </>
  );
};
